import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { useAlert } from '../../context/AlertContext';
import { usePermissions } from '../../context/PermissionContext';

const AuditTemplates = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const { hasPermission } = usePermissions();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  // Suggested checklist items for the chosen audit type. A PROPOSAL: nothing is
  // added to the template until the user picks it, so `suggestions` and
  // `templateForm.checklist_items` stay separate lists.
  const [suggestions, setSuggestions] = useState(null);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [pickedSuggestions, setPickedSuggestions] = useState(() => new Set());
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [templateForm, setTemplateForm] = useState({
    template_name: '',
    template_version: '1.0',
    audit_type: 'ISO 45001',
    description: '',
    checklist_items: []
  });
  const [checklistItemForm, setChecklistItemForm] = useState({
    question: '',
    category: 'Safety',
    is_critical: false,
    guidance_notes: ''
  });
  const [customAuditType, setCustomAuditType] = useState('');
  const [isCustomAuditType, setIsCustomAuditType] = useState(false);
  const [excelImporting, setExcelImporting] = useState(false);
  const excelInputRef = useRef(null);

  // Keep in step with backend AuditType (db/models/tenant/inspection.py).
  // Internal Audit, Gemba Walk and Inspection were added for the Safety
  // Dashboard's audit-type chart, which groups on whatever is recorded here.
  const auditTypes = ['ISO 45001', 'WSH Singapore', 'Internal Audit', 'Gemba Walk',
    'Inspection', 'Contractor Audit', 'Internal Governance', 'Fire Safety',
    'Electrical Safety', 'Environmental'];
  const categories = ['Safety', 'Environment', 'Fire', 'Equipment', 'Behaviour', 'Housekeeping'];

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/audit-templates`, { headers });
      setTemplates(res.data);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      const isCustom = !auditTypes.includes(template.audit_type);
      setIsCustomAuditType(isCustom);
      setCustomAuditType(isCustom ? template.audit_type : '');
      setTemplateForm({
        template_name: template.template_name,
        template_version: template.template_version,
        audit_type: template.audit_type,
        description: template.description || '',
        checklist_items: template.checklist_items || []
      });
    } else {
      setEditingTemplate(null);
      setIsCustomAuditType(false);
      setCustomAuditType('');
      setTemplateForm({
        template_name: '',
        template_version: '1.0',
        audit_type: 'ISO 45001',
        description: '',
        checklist_items: []
      });
    }
    setShowModal(true);
  };

  // Re-fetch whenever the chosen type changes. Scoped to an open modal so
  // browsing the template list makes no requests.
  useEffect(() => {
    if (!showModal || !templateForm.audit_type) {
      setSuggestions(null);
      return undefined;
    }
    const controller = new AbortController();
    setSuggestLoading(true);
    axios
      .get(`${API}/audit-templates/checklist-suggestions`, {
        params: { audit_type: templateForm.audit_type },
        signal: controller.signal,
      })
      .then(({ data }) => {
        setSuggestions(data);
        setPickedSuggestions(new Set());
      })
      .catch((err) => {
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
        // A missing suggestion list must not block building a template by hand.
        setSuggestions(null);
      })
      .finally(() => setSuggestLoading(false));
    return () => controller.abort();
  }, [showModal, templateForm.audit_type]);

  const toggleSuggestion = (key) => setPickedSuggestions((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // Already on the template? Compared on the QUESTION text, because an added
  // item keeps no link back to the suggestion it came from — and the user may
  // have edited it since.
  const existingQuestions = new Set(
    templateForm.checklist_items.map((i) => (i.question || '').trim().toLowerCase()),
  );

  const addPickedSuggestions = () => {
    const items = (suggestions?.items || []).filter(
      (s) => pickedSuggestions.has(s.suggestion_key)
        && !existingQuestions.has((s.question || '').trim().toLowerCase()),
    );
    if (!items.length) return;
    setTemplateForm((form) => ({
      ...form,
      checklist_items: [
        ...form.checklist_items,
        ...items.map((s, n) => ({
          // Same id shape the manual form uses; +n so a batch cannot collide.
          id: `item-${Date.now()}-${n}`,
          question: s.question,
          category: s.category,
          is_critical: s.is_critical,
          guidance_notes: s.guidance_notes || '',
        })),
      ],
    }));
    setPickedSuggestions(new Set());
    showInfo(`${items.length} item${items.length === 1 ? '' : 's'} added — edit or remove any of them below.`);
  };

  const addChecklistItem = () => {
    if (!checklistItemForm.question.trim()) return;
    
    setTemplateForm({
      ...templateForm,
      checklist_items: [
        ...templateForm.checklist_items,
        {
          id: `item-${Date.now()}`,
          ...checklistItemForm
        }
      ]
    });
    
    setChecklistItemForm({
      question: '',
      category: 'Safety',
      is_critical: false,
      guidance_notes: ''
    });
  };

  const removeChecklistItem = (itemId) => {
    setTemplateForm({
      ...templateForm,
      checklist_items: templateForm.checklist_items.filter(item => item.id !== itemId)
    });
  };

  const handleExcelImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';

    setExcelImporting(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/audit-templates/import-excel`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      fetchTemplates();
      showSuccess(res.data.message || 'Template imported successfully');
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to import Excel template');
    } finally {
      setExcelImporting(false);
    }
  };

  const saveTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

      if (editingTemplate) {
        await axios.put(`${API}/audit-templates/${editingTemplate.id}`, templateForm, { headers });
      } else {
        await axios.post(`${API}/audit-templates`, templateForm, { headers });
      }

      setShowModal(false);
      fetchTemplates();
      showSuccess(editingTemplate ? 'Template updated successfully' : 'Template created successfully');
    } catch (err) {
      console.error('Failed to save template:', err);
      showError('Failed to save template');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="p-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/audits')}
            className="text-blue-600 hover:underline mb-4 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Audits Dashboard
          </button>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">📋 Audit Templates</h1>
              <p className="text-gray-600 mt-1">{templates.length} template(s) available</p>
            </div>
            {hasPermission('audits', 'create') && (
              <div className="flex gap-2">
                <input
                  ref={excelInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={handleExcelImport}
                />
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const res = await axios.get(`${API}/audit-templates/sample-excel`, {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', 'audit_template_sample.xlsx');
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (err) {
                      showError('Failed to download sample file');
                    }
                  }}
                  className="bg-blue-600 text-white px-5 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download Sample
                </button>
                <button
                  onClick={() => excelInputRef.current && excelInputRef.current.click()}
                  disabled={excelImporting}
                  className="bg-green-600 text-white px-5 py-3 rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  {excelImporting ? 'Importing...' : 'Import Excel'}
                </button>
                <button
                  onClick={() => openModal()}
                  className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create Template
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map(template => (
            <div key={template.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <span className="px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-bold rounded-full">
                  {template.audit_type}
                </span>
                {template.is_active ? (
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">Active</span>
                ) : (
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">Inactive</span>
                )}
              </div>
              
              <h3 className="text-lg font-bold text-gray-800 mb-2">{template.template_name}</h3>
              <p className="text-sm text-gray-600 mb-1">Version: {template.template_version}</p>
              <p className="text-sm text-gray-600 mb-3">{template.checklist_items.length} checklist items</p>
              
              {template.description && (
                <p className="text-xs text-gray-500 mb-4 line-clamp-2">{template.description}</p>
              )}
              
              <div className="flex gap-2">
                <button
                  onClick={() => openModal(template)}
                  className="flex-1 text-blue-600 border border-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 text-sm"
                >
                  View/Edit
                </button>
                {hasPermission('audits', 'execute') && (
                  <button
                    onClick={() => navigate('/audits/schedule', { state: { template } })}
                    className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 text-sm"
                  >
                    Use Template
                  </button>
                )}
              </div>
            </div>
          ))}
          
          {templates.length === 0 && (
            <div className="col-span-full text-center py-12 bg-gray-50 rounded-xl">
              <p className="text-gray-600">No templates yet. Create your first audit template!</p>
            </div>
          )}
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingTemplate ? 'Edit Template' : 'Create New Template'}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto" style={{maxHeight: 'calc(90vh - 140px)'}}>
                {/* Template Info */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Template Name *</label>
                    <input
                      type="text"
                      value={templateForm.template_name}
                      onChange={(e) => setTemplateForm({...templateForm, template_name: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="e.g., Monthly Site Walk Checklist"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Audit Type *</label>
                    <select
                      value={isCustomAuditType ? 'Other' : templateForm.audit_type}
                      onChange={(e) => {
                        if (e.target.value === 'Other') {
                          setIsCustomAuditType(true);
                          setCustomAuditType('');
                          setTemplateForm({...templateForm, audit_type: ''});
                        } else {
                          setIsCustomAuditType(false);
                          setCustomAuditType('');
                          setTemplateForm({...templateForm, audit_type: e.target.value});
                        }
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      {auditTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                      <option value="Other">Other (specify)</option>
                    </select>
                    {isCustomAuditType && (
                      <input
                        type="text"
                        value={customAuditType}
                        onChange={(e) => {
                          setCustomAuditType(e.target.value);
                          setTemplateForm({...templateForm, audit_type: e.target.value});
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg mt-2"
                        placeholder="Enter custom audit type"
                        autoFocus
                      />
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Version</label>
                    <input
                      type="text"
                      value={templateForm.template_version}
                      onChange={(e) => setTemplateForm({...templateForm, template_version: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="1.0"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                    <textarea
                      value={templateForm.description}
                      onChange={(e) => setTemplateForm({...templateForm, description: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      rows={2}
                      placeholder="Brief description of this audit template"
                    />
                  </div>
                </div>
                
                {/* Add Checklist Item */}
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <h3 className="font-bold text-gray-800 mb-3">Add Checklist Item</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <input
                        type="text"
                        value={checklistItemForm.question}
                        onChange={(e) => setChecklistItemForm({...checklistItemForm, question: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="Checklist question/item"
                      />
                    </div>
                    
                    <select
                      value={checklistItemForm.category}
                      onChange={(e) => setChecklistItemForm({...checklistItemForm, category: e.target.value})}
                      className="px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    
                    <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white">
                      <input
                        type="checkbox"
                        checked={checklistItemForm.is_critical}
                        onChange={(e) => setChecklistItemForm({...checklistItemForm, is_critical: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Critical Item</span>
                    </label>
                    
                    <div className="col-span-2">
                      <input
                        type="text"
                        value={checklistItemForm.guidance_notes}
                        onChange={(e) => setChecklistItemForm({...checklistItemForm, guidance_notes: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="Guidance notes (optional)"
                      />
                    </div>
                    
                    <button
                      onClick={addChecklistItem}
                      className="col-span-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                    >
                      + Add Item
                    </button>
                  </div>
                </div>
                
                {/* Suggested items for the chosen audit type.
                    A PROPOSAL — nothing lands on the template until the user
                    ticks it, which is why this list is separate from the
                    checklist below and shows what is already added. */}
                {(suggestLoading || (suggestions && suggestions.items.length > 0)) && (
                  <div className="mb-6 border border-blue-200 bg-blue-50/60 rounded-lg">
                    <div className="px-4 py-3 flex flex-wrap items-center gap-2 border-b border-blue-200">
                      <h3 className="font-bold text-gray-800">
                        Suggested for {templateForm.audit_type || 'this audit type'}
                      </h3>
                      {suggestions && (
                        <span className="text-xs text-gray-600">
                          {suggestions.items.length} item{suggestions.items.length === 1 ? '' : 's'}
                          {!suggestions.recognised && ' — general items only'}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowSuggestions((v) => !v)}
                        className="ml-auto text-xs font-medium text-blue-700 hover:text-blue-900"
                      >
                        {showSuggestions ? 'Hide' : 'Show'}
                      </button>
                    </div>

                    {showSuggestions && (
                      <div className="p-4">
                        {suggestLoading && (
                          <p className="text-sm text-gray-500 py-2">Loading suggestions…</p>
                        )}
                        {!suggestLoading && suggestions && (
                          <>
                            <p className="text-[11px] text-gray-600 mb-3">{suggestions.note}</p>
                            <div className="space-y-1.5 max-h-72 overflow-y-auto">
                              {suggestions.items.map((item) => {
                                const already = existingQuestions.has(
                                  (item.question || '').trim().toLowerCase());
                                return (
                                  <label
                                    key={item.suggestion_key}
                                    className={`flex items-start gap-2.5 bg-white border rounded-lg p-2.5 ${
                                      already ? 'border-gray-200 opacity-60'
                                        : 'border-gray-200 cursor-pointer hover:border-blue-300'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      className="mt-1"
                                      disabled={already}
                                      checked={pickedSuggestions.has(item.suggestion_key)}
                                      onChange={() => toggleSuggestion(item.suggestion_key)}
                                    />
                                    <span className="min-w-0 flex-1">
                                      <span className="flex flex-wrap items-center gap-1.5">
                                        <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] font-medium">
                                          {item.category}
                                        </span>
                                        {item.is_critical && (
                                          <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-semibold">
                                            Critical
                                          </span>
                                        )}
                                        {item.source === 'baseline' && (
                                          <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px]">
                                            Every audit
                                          </span>
                                        )}
                                        {already && (
                                          <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-medium">
                                            Added
                                          </span>
                                        )}
                                      </span>
                                      <span className="block text-sm text-gray-800 mt-0.5">
                                        {item.question}
                                      </span>
                                      {item.guidance_notes && (
                                        <span className="block text-[11px] text-gray-500 mt-0.5">
                                          {item.guidance_notes}
                                        </span>
                                      )}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>

                            <div className="mt-3 flex flex-wrap items-center gap-3">
                              <button
                                type="button"
                                onClick={addPickedSuggestions}
                                disabled={pickedSuggestions.size === 0}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                              >
                                Add {pickedSuggestions.size || ''} to checklist
                              </button>
                              <button
                                type="button"
                                onClick={() => setPickedSuggestions(new Set(
                                  suggestions.items
                                    .filter((i) => !existingQuestions.has(
                                      (i.question || '').trim().toLowerCase()))
                                    .map((i) => i.suggestion_key)))}
                                className="text-xs font-medium text-blue-700 hover:underline"
                              >
                                Select all not yet added
                              </button>
                              <span className="text-[11px] text-gray-500 ml-auto">
                                library {suggestions.catalog_version}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Checklist Items List */}
                <div>
                  <h3 className="font-bold text-gray-800 mb-3">Checklist Items ({templateForm.checklist_items.length})</h3>
                  {templateForm.checklist_items.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {templateForm.checklist_items.map((item, index) => (
                        <div key={item.id} className="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-lg">
                          <span className="text-sm font-bold text-gray-500 mt-1">{index + 1}.</span>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-gray-800">{item.question}</p>
                            <div className="flex gap-2 mt-1">
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">{item.category}</span>
                              {item.is_critical && (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Critical</span>
                              )}
                            </div>
                            {item.guidance_notes && (
                              <p className="text-xs text-gray-600 mt-1">Note: {item.guidance_notes}</p>
                            )}
                          </div>
                          <button
                            onClick={() => removeChecklistItem(item.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm text-center py-4">No checklist items added yet</p>
                  )}
                </div>
              </div>
              
              <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex gap-3 border-t border-gray-200">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={saveTemplate}
                  disabled={!templateForm.template_name || !templateForm.audit_type || templateForm.checklist_items.length === 0}
                  className="flex-1 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {editingTemplate ? 'Update Template' : 'Create Template'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
      </div>
  );
};

export default AuditTemplates;
